/**
 * ProcessChanges.jsx: policy change to process change (24 Oct 2026).
 *
 * Every policy version a change pack publishes opens one process change:
 * what in CaseMinder must now change for the process to follow the policy.
 * Claude drafts it, the policy's owner edits it and sends it to a director,
 * and a director signs it off or sends it back. Nothing in CaseMinder
 * changes from this page.
 * Server: /api/policy-process-changes (backend/hub/policy_process_change.py).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Workflow, Sparkles, Send, CheckCircle2, Undo2, Plus, Trash2, ExternalLink, Link2, ClipboardCopy, Check } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

const BASE = `${API}/policy-process-changes`;
const INPUT =
  "px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200";
const STATE = {
  open: ["To draft", "bg-amber-900/50 text-amber-200"],
  drafted: ["Drafted", "bg-amber-900/50 text-amber-200"],
  returned: ["Sent back", "bg-red-900/50 text-red-200"],
  with_director: ["With a director", "bg-indigo-900/50 text-indigo-200"],
  signed_off: ["Signed off: to build", "bg-emerald-900/50 text-emerald-200"],
  closed: ["Closed", "bg-slate-800 text-slate-400"],
};
const EDITABLE = ["open", "drafted", "returned"];
const when = (iso, withTime = true) => {
  if (!iso) return "";
  const utc = /([zZ]|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`;
  return new Date(utc).toLocaleString("en-GB", withTime
    ? { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/London" }
    : { dateStyle: "medium", timeZone: "Europe/London" });
};
// One signed-off change: ticked off as done, with what was done, once it is live.
function ChangeDone({ rec, index, onDone }) {
  const [note, setNote] = useState("");
  return (
    <span className="inline-flex flex-wrap items-center gap-1 ml-2">
      <input className={`${INPUT} text-xs w-56`} placeholder="What was done, or the PR link" value={note}
        onChange={(e) => setNote(e.target.value)} data-testid={`pc-done-note-${rec.ref}-${index}`} />
      <button type="button" disabled={!note.trim()} onClick={() => onDone(index, note)} title="Mark this change done: it is live in CaseMinder"
        data-testid={`pc-done-${rec.ref}-${index}`}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-800 hover:bg-emerald-700 text-xs text-white disabled:opacity-50">
        <Check size={12} /> Done
      </button>
    </span>
  );
}

const blankChange = () => ({ area: "", what: "", why: "", section_ref: "", binding_id: null });

function DraftForm({ rec, onSaved, setError }) {
  const start = rec.draft || {};
  const [note, setNote] = useState(start.staff_note || "");
  const [changes, setChanges] = useState(start.changes?.length ? start.changes : [blankChange()]);
  const [noChange, setNoChange] = useState(!!start.no_change);
  const [why, setWhy] = useState(start.no_change_reason || "");
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);

  const ask = async () => {
    setAsking(true);
    setError("");
    try {
      const r = await axios.post(`${BASE}/${rec._id}/draft-with-claude`, {}, { headers: authHeaders() });
      const d = r.data.draft;
      setNote(d.staff_note || "");
      setNoChange(!!d.no_change);
      setWhy(d.no_change_reason || "");
      setChanges(d.changes?.length ? d.changes : [blankChange()]);
    } catch (err) {
      setError(errorMessage(err, "Claude could not draft this"));
    } finally {
      setAsking(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.put(`${BASE}/${rec._id}/draft`, {
        staff_note: note, changes: noChange ? [] : changes.filter((c) => (c.what || "").trim()),
        no_change: noChange, no_change_reason: noChange ? why : "",
      }, { headers: authHeaders() });
      await onSaved();
    } catch (err) {
      setError(errorMessage(err, "Could not save the draft"));
    } finally {
      setSaving(false);
    }
  };

  const set = (i, key, value) => setChanges((cs) => cs.map((c, j) => (j === i ? { ...c, [key]: value } : c)));

  return (
    <div className="space-y-2 p-2 rounded-md border border-slate-700/60" data-testid={`pc-form-${rec.ref}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={ask} disabled={asking} data-testid={`pc-claude-${rec.ref}`}
          title="Claude reads the changed sections and the linked steps and drafts what CaseMinder must change. It fills the form only: nothing is saved until you save."
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-800 hover:bg-indigo-700 text-xs text-white disabled:opacity-60">
          <Sparkles size={12} /> {asking ? "Claude is drafting…" : "Draft with Claude"}
        </button>
        <label className="inline-flex items-center gap-1 text-xs text-slate-300">
          <input type="checkbox" checked={noChange} onChange={(e) => setNoChange(e.target.checked)}
            data-testid={`pc-nochange-${rec.ref}`} />
          No process change needed
        </label>
      </div>
      <label className="block text-xs text-slate-400">
        What staff now do differently
        <textarea className={`${INPUT} w-full mt-1`} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
          data-testid={`pc-note-${rec.ref}`} />
      </label>
      {noChange ? (
        <input className={`${INPUT} w-full`} placeholder="Why nothing in CaseMinder needs to change" value={why}
          onChange={(e) => setWhy(e.target.value)} data-testid={`pc-nochange-reason-${rec.ref}`} />
      ) : (
        <div className="space-y-2">
          {changes.map((c, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[10rem_1fr_1fr_4rem_auto] gap-1 items-start"
              data-testid={`pc-change-${rec.ref}-${i}`}>
              <input className={INPUT} placeholder="Area" value={c.area} onChange={(e) => set(i, "area", e.target.value)} />
              <textarea className={INPUT} rows={2} placeholder="What to change" value={c.what}
                onChange={(e) => set(i, "what", e.target.value)} />
              <textarea className={INPUT} rows={2} placeholder="Why: the policy wording" value={c.why}
                onChange={(e) => set(i, "why", e.target.value)} />
              <input className={INPUT} placeholder="§" value={c.section_ref}
                onChange={(e) => set(i, "section_ref", e.target.value)} />
              <button type="button" title="Remove" onClick={() => setChanges((cs) => cs.filter((_, j) => j !== i))}
                className="p-1 text-slate-400 hover:text-red-300"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setChanges((cs) => [...cs, blankChange()])}
            className="inline-flex items-center gap-1 text-xs text-sky-300"><Plus size={12} /> Add a change</button>
        </div>
      )}
      <button type="button" onClick={save} disabled={saving} data-testid={`pc-save-${rec.ref}`}
        className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200 hover:border-sky-400 disabled:opacity-60">
        {saving ? "Saving…" : "Save draft"}
      </button>
    </div>
  );
}

function Record({ rec, reload, setError }) {
  const [label, cls] = STATE[rec.status] || [rec.status, "bg-slate-800 text-slate-300"];
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState("");
  const [brief, setBrief] = useState("");
  const [copied, setCopied] = useState(false);

  const showBrief = async () => {
    setError("");
    try {
      const r = await axios.get(`${BASE}/${rec._id}/build-brief`, { headers: authHeaders() });
      setBrief(r.data.brief);
      try {
        await navigator.clipboard.writeText(r.data.brief);
        setCopied(true);
      } catch (e) {
        setCopied(false);  // the brief is on screen to copy by hand
      }
    } catch (err) {
      setError(errorMessage(err, "Could not get the build brief"));
    }
  };
  const draft = rec.draft;

  const post = async (url, body, fallback) => {
    setError("");
    try {
      await axios.post(url, body, { headers: authHeaders() });
      await reload();
    } catch (err) {
      setError(errorMessage(err, fallback));
    }
  };

  return (
    <article className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2" data-testid={`pc-record-${rec.ref}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-mono text-slate-400">{rec.ref}</span>
        <span className="text-sm font-semibold text-slate-100">{rec.policy_label}</span>
        <span className="text-xs text-slate-400">{rec.from_version_label} → {rec.to_version_label}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{label}</span>
        <span className={`text-xs ${rec.overdue ? "text-red-300 font-semibold" : "text-slate-400"}`} data-testid={`pc-due-${rec.ref}`}>
          {rec.overdue ? "Overdue: was due " : "Due "}{when(rec.due_at, false)}
        </span>
      </div>
      {rec.policy_reason ? <div className="text-xs text-slate-300">Policy change: {rec.policy_reason}</div> : null}

      <details className="text-xs text-slate-300">
        <summary className="cursor-pointer text-slate-400">
          {rec.sections_changed?.length || 0} section(s) changed · {rec.linked_steps?.length || 0} linked step(s)
          {(rec.linked_steps || []).some((s) => s.needs_reverification) ? " · some to re-verify" : ""}
        </summary>
        <div className="space-y-2 mt-2">
          {(rec.sections_changed || []).map((s) => (
            <div key={s.section_ref} className="grid md:grid-cols-2 gap-2">
              <pre className="whitespace-pre-wrap p-2 rounded bg-slate-950/60 text-slate-400">§{s.section_ref} before{"\n"}{s.before || "(not there)"}</pre>
              <pre className="whitespace-pre-wrap p-2 rounded bg-slate-950/60 text-slate-200">§{s.section_ref} now{"\n"}{s.after || "(removed)"}</pre>
            </div>
          ))}
          {(rec.linked_steps || []).length ? (
            <ul className="space-y-0.5">
              {rec.linked_steps.map((s) => (
                <li key={s.binding_id} className="inline-flex items-center gap-1 mr-3">
                  <Link2 size={10} /> §{s.section_ref} {s.artefact}
                  {s.needs_reverification ? <span className="text-amber-300"> (section changed)</span> : null}
                </li>
              ))}
            </ul>
          ) : <div className="text-slate-500">No CaseMinder steps are linked to this policy, so Claude checks the rest of CaseMinder.</div>}
          {(rec.scan_items || []).map((s) => (
            <a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="text-sky-300 underline inline-flex items-center gap-1 mr-3">
              {s.title} <ExternalLink size={10} />
            </a>
          ))}
        </div>
      </details>

      {draft && !editing ? (
        <div className="text-xs text-slate-200 space-y-1" data-testid={`pc-draft-${rec.ref}`}>
          {draft.staff_note ? <div><span className="text-slate-400">Staff: </span>{draft.staff_note}</div> : null}
          {draft.no_change ? (
            <div><span className="text-slate-400">No process change needed: </span>{draft.no_change_reason}</div>
          ) : (
            <ol className="list-decimal pl-5 space-y-0.5">
              {draft.changes.map((c, i) => (
                <li key={i} data-testid={`pc-change-row-${rec.ref}-${i}`}><span className="font-semibold">{c.area}</span>: {c.what}
                  {c.why ? <span className="text-slate-400"> ({c.section_ref ? `§${c.section_ref}: ` : ""}{c.why})</span> : null}
                  {c.state === "done" ? (
                    <span className="ml-2 text-emerald-300">Done {when(c.done_at)} by {c.done_by}: {c.done_note}</span>
                  ) : c.state === "to_build" && rec.may_mark_done ? (
                    <ChangeDone rec={rec} index={i}
                      onDone={(index, note) => post(`${BASE}/${rec._id}/changes/${index}/done`, { note }, "Could not mark it done")} />
                  ) : c.state === "to_build" ? <span className="ml-2 text-amber-300">To build</span> : null}
                </li>
              ))}
            </ol>
          )}
          <div className="text-slate-500">Saved by {draft.saved_by} {when(draft.saved_at)}</div>
        </div>
      ) : null}
      {rec.status === "signed_off" && draft && !draft.no_change ? (
        <div className="space-y-1">
          <button type="button" onClick={showBrief} data-testid={`pc-brief-${rec.ref}`}
            title="The signed-off changes as one brief to build from. Copied to the clipboard."
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200 hover:border-sky-400">
            <ClipboardCopy size={12} /> {copied ? "Build brief copied" : "Build brief"}
          </button>
          {brief ? <pre className="whitespace-pre-wrap text-xs p-2 rounded bg-slate-950/60 text-slate-300" data-testid={`pc-brief-text-${rec.ref}`}>{brief}</pre> : null}
        </div>
      ) : null}
      {rec.status === "closed" && rec.closed_reason ? (
        <div className="text-xs text-slate-400" data-testid={`pc-closed-${rec.ref}`}>Closed {when(rec.closed_at)}: {rec.closed_reason}
          {(rec.verified_links || []).length ? `; linked steps re-verified: ${rec.verified_links.join(", ")}` : ""}</div>
      ) : null}
      {rec.status === "returned" && rec.returned_comment ? (
        <div className="text-xs text-red-200">Sent back by {rec.returned_by}: {rec.returned_comment}</div>
      ) : null}
      {rec.signed_off_by ? (
        <div className="text-xs text-emerald-200">Signed off by {rec.signed_off_by} {when(rec.signed_off_at)}
          {rec.signoff_comment ? `: ${rec.signoff_comment}` : ""}</div>
      ) : null}

      {rec.may_own && EDITABLE.includes(rec.status) ? (
        editing || !draft ? (
          <DraftForm rec={rec} setError={setError} onSaved={async () => { setEditing(false); await reload(); }} />
        ) : (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setEditing(true)} data-testid={`pc-edit-${rec.ref}`}
              className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200 hover:border-sky-400">Edit the draft</button>
            {rec.status === "drafted" ? (
              <button type="button" onClick={() => post(`${BASE}/${rec._id}/submit`, {}, "Could not send it")} data-testid={`pc-submit-${rec.ref}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-700 hover:bg-sky-600 text-xs text-white">
                <Send size={12} /> Send to a director
              </button>
            ) : null}
          </div>
        )
      ) : null}

      {rec.may_sign_off ? (
        <div className="flex flex-wrap items-center gap-2" data-testid={`pc-signoff-${rec.ref}`}>
          <input className={`${INPUT} flex-1 min-w-[12rem]`} placeholder="Comment (needed to send back)" value={comment}
            onChange={(e) => setComment(e.target.value)} data-testid={`pc-comment-${rec.ref}`} />
          <button type="button" onClick={() => post(`${BASE}/${rec._id}/sign-off`, { decision: "approve", comment }, "Could not sign off")}
            data-testid={`pc-approve-${rec.ref}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-800 hover:bg-emerald-700 text-xs text-white">
            <CheckCircle2 size={12} /> Sign off
          </button>
          <button type="button" disabled={!comment.trim()} onClick={() => post(`${BASE}/${rec._id}/sign-off`, { decision: "return", comment }, "Could not send it back")}
            data-testid={`pc-return-${rec.ref}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-red-700 text-xs text-red-200 disabled:opacity-50">
            <Undo2 size={12} /> Send back
          </button>
        </div>
      ) : null}
    </article>
  );
}

export default function ProcessChanges() {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState("");
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(BASE, { headers: authHeaders() });
      setData(r.data);
    } catch (err) {
      setError(errorMessage(err, "Could not load the process changes"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (data === undefined && !error) return <div className="p-6 text-sm text-slate-400">Loading the process changes…</div>;
  const records = (data?.records || []).filter((r) => showClosed || r.status !== "closed");

  return (
    <div className="p-6 max-w-5xl space-y-5" data-testid="pc-page">
      <header className="flex flex-wrap items-center gap-3">
        <Workflow size={20} className="text-indigo-300" />
        <h1 className="text-xl font-semibold text-slate-100">Process changes</h1>
        {data ? (
          <span className="text-xs text-slate-400" data-testid="pc-counts">
            {data.open} open · {data.with_director} with a director
            {data.overdue ? <span className="text-red-300 font-semibold"> · {data.overdue} overdue</span> : null}
          </span>
        ) : null}
      </header>
      <p className="text-sm text-slate-400 max-w-3xl">
        Each published policy change opens one of these: what CaseMinder must now do so the process follows the new policy.
        Claude drafts it, the policy's owner checks it and sends it to a director, and a director signs it off.
        Nothing in CaseMinder changes until it is signed off.
      </p>
      {error ? <div className="text-sm text-red-300" data-testid="pc-error">{error}</div> : null}
      <label className="inline-flex items-center gap-1 text-xs text-slate-400">
        <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed
      </label>
      {records.length ? (
        <section className="space-y-3">
          {records.map((r) => <Record key={r._id} rec={r} reload={load} setError={setError} />)}
        </section>
      ) : (
        <div className="text-sm text-slate-400" data-testid="pc-none">
          No process changes open. They open when a <a href="/compliance/change-pack" className="text-sky-300 underline">change pack</a> is published.
        </div>
      )}
    </div>
  );
}
